import { z } from 'zod';

/** Largest pixel density multiplier (retina 2x, print-quality 3x). */
export const MAX_SCALE = 3;
/** Upscaling stops here: an image side of at most this many pixels… */
export const MAX_SCALED_SIDE = 8000;
/** …and at most this many pixels in all (40 Mpx, a large print). */
export const MAX_SCALED_PIXELS = 40_000_000;

/**
 * Largest scale for a template of this size: 3x, less for a large canvas so that the image stays
 * within 8000 px per side and 40 Mpx. A template is always available at its own size (1x), and
 * PDF and video don't scale at all.
 */
export function maxScaleFor(width: number, height: number): number {
  const bySide = MAX_SCALED_SIDE / Math.max(width, height);
  const byArea = Math.sqrt(MAX_SCALED_PIXELS / (width * height));
  return Math.max(1, Math.min(MAX_SCALE, Math.floor(Math.min(bySide, byArea) * 100) / 100));
}

export const IMAGE_FORMATS = ['png', 'jpg', 'webp', 'pdf'] as const;
/** Animated output: the template's motion rendered frame by frame (see ./motion). */
export const VIDEO_FORMATS = ['mp4', 'webm', 'gif'] as const;
export const RENDER_FORMATS = [...IMAGE_FORMATS, ...VIDEO_FORMATS] as const;
export type RenderFormat = (typeof RENDER_FORMATS)[number];
export type ImageFormat = (typeof IMAGE_FORMATS)[number];
export type VideoFormat = (typeof VIDEO_FORMATS)[number];

export function isVideoFormat(format: string): format is VideoFormat {
  return (VIDEO_FORMATS as readonly string[]).includes(format);
}

export const RENDER_STATUSES = ['queued', 'rendering', 'done', 'failed'] as const;
export type RenderStatus = (typeof RENDER_STATUSES)[number];

export const MIME_TYPES: Record<RenderFormat, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  webp: 'image/webp',
  pdf: 'application/pdf',
  mp4: 'video/mp4',
  webm: 'video/webm',
  gif: 'image/gif',
};

export const renderFormatSchema = z
  .string()
  .transform((v) => (v.toLowerCase() === 'jpeg' ? 'jpg' : v.toLowerCase()))
  .pipe(z.enum(RENDER_FORMATS));

export const renderOptionsSchema = z.object({
  format: renderFormatSchema.default('png'),
  /** JPG / WEBP quality. */
  quality: z.number().int().min(1).max(100).default(90),
  /** Pixel density multiplier: 2 = retina. */
  scale: z.number().min(0.1).max(MAX_SCALE).default(1),
  /** Output file name without extension (optional). */
  filename: z.string().max(200).optional(),
});
export type RenderOptions = z.infer<typeof renderOptionsSchema>;

/** Placeholders: {{template}}, {{index}}, {{format}}, {{date}}, {{id}}, {{row.<column>}}, {{<layer>}} */
export function renderFilename(
  pattern: string,
  ctx: {
    template?: string;
    index?: number;
    format?: string;
    id?: string;
    row?: Record<string, unknown>;
    date?: Date;
  },
): string {
  const date = (ctx.date ?? new Date()).toISOString().slice(0, 10);
  const value = pattern.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, key: string) => {
    if (key === 'template') return ctx.template ?? '';
    if (key === 'index') return String(ctx.index ?? 0);
    if (key === 'format') return ctx.format ?? '';
    if (key === 'date') return date;
    if (key === 'id') return ctx.id ?? '';
    const rowKey = key.startsWith('row.') ? key.slice(4) : key;
    const v = ctx.row?.[rowKey];
    return v === undefined || v === null ? '' : String(v);
  });
  return sanitizeFilename(value) || ctx.id || 'image';
}

export function sanitizeFilename(name: string): string {
  return name
    .normalize('NFC')
    .replace(/[\u0000-\u001f<>:"/\\|?*]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^[\s.-]+|[\s.]+$/g, '')
    .slice(0, 150);
}

/** Webhook event names. */
export const WEBHOOK_EVENTS = ['render.completed', 'render.failed', 'batch.completed'] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

/** Contract between the render worker and the render page running in headless Chromium. */
export interface RenderPagePayload {
  doc: import('./template').TemplateDoc;
  fontCss: string;
  watermark: boolean;
  /**
   * The template before modifications: laid out first (without images) so the quality check
   * compares fitted text sizes with the template's own, not with the design font size.
   */
  baseline?: import('./template').TemplateDoc;
  /** Families that must be loaded before taking the screenshot. */
  fonts: Array<{ family: string; weight: number; style: string; sample: string }>;
}

export interface RenderPageResult {
  warnings: string[];
  issues: RenderIssue[];
}

/** Quality problems found in a finished render (see scene/inspect.ts). */
export const RENDER_ISSUE_KINDS = [
  /** Text doesn't fit its box even at the smallest size: it is cut off. */
  'text_overflow',
  /** Auto-fit had to shrink the text a lot (below 60% of the design size). */
  'text_shrunk',
  /** The image didn't load: an empty frame was rendered. */
  'image_missing',
  /** The image is stretched beyond its resolution and looks blurry. */
  'image_lowres',
  /** The layer ended up outside the canvas (e.g. moved through the API). */
  'offcanvas',
] as const;
export type RenderIssueKind = (typeof RENDER_ISSUE_KINDS)[number];

export interface RenderIssue {
  layer: string;
  kind: RenderIssueKind;
  /** Numbers for the message: font sizes, image pixels… */
  detail?: Record<string, number>;
}
